import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Button, Card, Tag, Typography } from 'antd';
// API
import { GET_AGENCIES } from 'api/campaignV2/queries';
// Types
import {
  GetAgencies,
  GetAgenciesVariables,
} from 'api/campaignV2/types/GetAgencies';
import { AgencyListOrderBy, SortDirection } from 'api/graphql-global-types';
import { AgencyRow } from 'helpers/teams/cbTeamOwnershipTransfer.types';
// UI
import Table, { SortedInfo, TableFilter } from 'ui/Table';
// Styles
import styles from './NewOwnerPicker.module.scss';

const { Text } = Typography;

type NewOwnerPickerProps = {
  ownerId: string;
  selectedTarget: AgencyRow | null;
  previewLoading: boolean;
  onPreview: (agency: AgencyRow) => void;
};

const NewOwnerPicker = ({
  ownerId,
  selectedTarget,
  previewLoading,
  onPreview,
}: NewOwnerPickerProps): JSX.Element => {
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSortInfo] = useState<SortedInfo<AgencyListOrderBy>>({
    order: SortDirection.DESC,
    key: AgencyListOrderBy.firstName,
  });
  const [searchFilters, setSearchFilters] = useState<TableFilter[]>([]);

  const getAgenciesInput = () => {
    const input: GetAgenciesVariables['input'] = {
      direction: sort.order,
      orderBy: sort.key,
      limit: pageSize,
      offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
    };

    searchFilters?.forEach(({ key, value }) => {
      (input as Record<string, unknown>)[key] = value;
    });

    return { input };
  };

  const { data: agenciesData, loading: agenciesLoading } = useQuery<
    GetAgencies,
    GetAgenciesVariables
  >(GET_AGENCIES, {
    variables: getAgenciesInput(),
    fetchPolicy: 'cache-and-network',
  });

  const formattedData: AgencyRow[] =
    agenciesData?.getAgencies.entities?.map(
      ({ id, firstName, lastName, email, agencyDetails }) => ({
        id,
        name: `${firstName || ''} ${lastName || ''}`.trim(),
        companyName: agencyDetails?.companyName || '',
        email: email || '',
      })
    ) || [];

  const columns = [
    {
      title: 'Agency name',
      dataIndex: 'name',
      align: 'left' as const,
      key: 'name',
      width: 200,
      withSearch: true,
      render: (_: string, item: AgencyRow) => <Text>{item.name}</Text>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      align: 'center' as const,
      width: 220,
      withSearch: true,
      key: 'email',
    },
    {
      title: 'Company',
      dataIndex: 'companyName',
      align: 'center' as const,
      width: 200,
      withSearch: true,
      key: 'companyName',
    },
    {
      title: 'Action',
      dataIndex: 'id',
      align: 'center' as const,
      width: 140,
      render: (id: string, item: AgencyRow) => {
        if (id === ownerId) {
          return <Tag color="blue">Current owner</Tag>;
        }

        return (
          <Button
            type={selectedTarget?.id === id ? 'primary' : 'default'}
            onClick={() => onPreview(item)}
            loading={previewLoading && selectedTarget?.id === id}
          >
            Preview
          </Button>
        );
      },
    },
  ];

  return (
    <Card title="New owner" className={styles.targetSection}>
      <Text type="secondary" className={styles.targetHelper}>
        Choose another brand account and preview the transfer. They must not
        already own a team, and must not have an active brand plan. Being a
        member of the current owner&apos;s team is fine.
      </Text>

      {selectedTarget && (
        <Alert
          className={styles.selectedTarget}
          type="info"
          showIcon
          message={`Previewing transfer to ${
            selectedTarget.name || selectedTarget.email
          }`}
        />
      )}

      <Table<AgencyRow, AgencyListOrderBy>
        columns={columns}
        data={formattedData}
        scroll={{ x: 760 }}
        loading={agenciesLoading || previewLoading}
        total={agenciesData?.getAgencies?.total}
        defaultPageSize={pageSize}
        setSearchFilters={setSearchFilters}
        setPageSize={setPageSize}
        setCurrentPage={setCurrentPage}
        searchFilters={searchFilters}
        sortInfo={sort}
        setSortInfo={setSortInfo}
      />
    </Card>
  );
};

export default NewOwnerPicker;
